import {
    ActionDescription,
    ActionKind,
    InputOutputTables,
    PropertyDescription,
    TableColumn,
    MutatingScreenKind,
} from "../description";
import { ActionDescriptor } from "./action-descriptor";
import { getAppFeatures, getEmailClient, SerializedComponent } from "../../components/SerializedApp";
import { panic } from "../../Support";
import {
    ComponentBuilder,
    ComponentConfiguratorContext,
    WireActionHydrator,
    WireInflationBackend,
} from "../components";
import { makeColumnProperty } from "../components/property-utils";
import { BaseActionHandler, tokenForProperty } from "./base";
import { DescriptionToken } from "./action-handler";
import { AdditionalEmailProperties, makeEmailPropertyDescriptors, makeEmailValues } from "../components/email";
import { asMaybeString, asString } from "../../computation-model/data";

interface SendEmailActionDescription extends ActionDescription, AdditionalEmailProperties {
    readonly kind: ActionKind.SendEmailWithArgs;
    readonly email?: PropertyDescription;
}

export class SendEmailWithArgsHandler extends BaseActionHandler<SendEmailActionDescription> {
    public readonly kind = ActionKind.SendEmailWithArgs;
    public readonly iconName = "mail";

    public getDescriptor(
        _desc: SendEmailActionDescription | undefined,
        _tables: InputOutputTables | undefined,
        _ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ActionDescriptor {
        return {
            name: "Compose Email",
            needsScreenContext: true,
            properties: makeEmailPropertyDescriptors("email", true, mutatingScreenKind),
        };
    }

    public newDefaultActionDescription(c: TableColumn): SendEmailActionDescription | undefined {
        if (c.type.kind !== "email-address") return undefined;
        return { kind: ActionKind.SendEmailWithArgs, email: makeColumnProperty(c.name) };
    }

    public makeAction(
        builder: ComponentBuilder,
        desc: SendEmailActionDescription,
        tables: InputOutputTables,
        screenContext: SerializedComponent | undefined
    ): SerializedComponent | undefined {
        const { b } = builder;
        if (screenContext === undefined) {
            return panic("Must have screen context for Compose Email action");
        }

        const emailValues = makeEmailValues(builder, tables, desc.email, desc, screenContext);
        if (emailValues === undefined) return undefined;

        const { to, subject, body, cc, bcc } = emailValues;

        return b.actionIfContextDefined(b.sendEmail(to, subject, body, cc, bcc));
    }

    public getTokenizedDescription(
        desc: SendEmailActionDescription,
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext
    ): readonly DescriptionToken[] | undefined {
        const columnToken = tokenForProperty(desc.email, tables, ccc);
        if (columnToken === undefined) return undefined;

        return [
            {
                kind: "string",
                value: "to ",
            },
            columnToken,
        ];
    }

    public inflate(ib: WireInflationBackend, desc: SendEmailActionDescription): WireActionHydrator | undefined {
        const emailClient = getEmailClient(getAppFeatures(ib.appDescription));
        const { appFacilities } = ib;

        const [recipientGetter] = ib.getValueGetterForProperty(desc.email, false);
        const [subjectGetter] = ib.getValueGetterForProperty(desc.subjectProperty, true);
        const [bodyGetter] = ib.getValueGetterForProperty(desc.bodyProperty, true);
        const [ccGetter] = ib.getValueGetterForProperty(desc.ccProperty, false);
        const [bccGetter] = ib.getValueGetterForProperty(desc.bccProperty, false);

        return vp => {
            const recipient = asString(recipientGetter(vp));
            const subject = asMaybeString(subjectGetter(vp));
            const body = asMaybeString(bodyGetter(vp));
            const cc = asMaybeString(ccGetter(vp));
            const bcc = asMaybeString(bccGetter(vp));

            const url = appFacilities.mailtoURL(emailClient, recipient, subject, body, cc, bcc);
            if (url === undefined) return undefined;

            return async ab => {
                ab.actionCallbacks.openLink(url);
                return true;
            };
        };
    }
}
